import { Modification, ModificationHandler, ModificationAction } from '@core/types';
import { ModificationError } from '@shared/errors';

export class TextModificationHandler implements ModificationHandler {
  apply(element: Element, modification: Modification): void {
    const text = modification.value;
    if (text === undefined || text === null) return;

    switch (modification.action) {
      case ModificationAction.SET_TEXT:
        const newText = String(text);
        const selectorKey = `${modification.selector}|${modification.action}|${newText}`;

        const debug = new URLSearchParams(window.location.search).get('debug') === 'true';
        if (debug) {
          console.log(
            '[Campaign] set_text called. Current:',
            element.textContent,
            'Expected:',
            newText,
          );
        }

        if (element.textContent !== newText) {
          if (debug) {
            console.log('[Campaign] Setting text:', element.textContent, '->', newText);
          }
          element.innerHTML = '';
          element.textContent = newText;
        }

        expectedTextBySelector.set(selectorKey, newText);

        if (!protectedElements.has(element)) {
          if (debug) {
            console.log('[Campaign] Attaching protection observer to element');
          }

          try {
            const protectionObserver = new MutationObserver((mutations) => {
              if (debug) {
                console.log('[Campaign] Protection observer fired! Mutations:', mutations.length);
              }

              for (const mutation of mutations) {
                if (mutation.type === 'childList' || mutation.type === 'characterData') {
                  if (element.textContent !== newText) {
                    if (debug) {
                      console.log(
                        '[Campaign] REVERTING! Text changed from',
                        newText,
                        'to',
                        element.textContent,
                      );
                    }
                    element.innerHTML = '';
                    element.textContent = newText;
                  }
                }
              }
            });

            protectionObserver.observe(element, {
              childList: true,
              characterData: true,
              subtree: true,
            });

            protectedElements.set(element, protectionObserver);

            if (debug) {
              console.log('[Campaign] Protection observer attached successfully');
            }
          } catch (error) {
            if (debug) {
              console.warn('[Campaign] Could not protect element:', error);
            }
          }
        }
        break;

      default:
        throw new ModificationError(
          `Unsupported action: ${modification.action}`,
          modification.action,
        );
    }
  }

  validate(modification: Modification): boolean {
    return modification.action === ModificationAction.SET_TEXT;
  }

  remove(element: Element): void {
    // Disconnect the protection observer for this element
    const observer = protectedElements.get(element);
    if (observer) {
      observer.disconnect();
      protectedElements.delete(element);
    }
  }
}

// Shared state for text modifications
const protectedElements = new WeakMap<Element, MutationObserver>();
const expectedTextBySelector = new Map<string, string>();
